/**
 * Mobile keyboard + browser autofill defaults for Apply's redux-form inputs
 * (F13, mobile UX audit 2026-10). Every text input goes through
 * `newThemeInput`, so deriving the attributes from the field name and the
 * format flags fixes the owner, business, contact and address steps in one
 * place. Explicit `autoComplete` / `inputMode` / `type` props always win.
 *
 * Field names follow the redux-form conventions used across the flows:
 * owner_<n>_first / _last / _email / _mobile / _title / _ownership / _ssn /
 * _dob_*, business_name / business_phone / business_website / business_ein /
 * business_revenue, <addressKey>_street / _suite / _city / _state / _zip.
 */
const NAME_RULES = [
  [/(^|_)first$/, { autoComplete: 'given-name' }],
  [/(^|_)last$/, { autoComplete: 'family-name' }],
  [/(^|_)email$/, { autoComplete: 'email', inputMode: 'email' }],
  [/(^|_)(mobile|phone)$/, { autoComplete: 'tel', inputMode: 'tel', type: 'tel' }],
  [/^business_name$/, { autoComplete: 'organization' }],
  [/_title$/, { autoComplete: 'organization-title' }],
  [/_street$/, { autoComplete: 'address-line1' }],
  [/_suite$/, { autoComplete: 'address-line2' }],
  [/_city$/, { autoComplete: 'address-level2' }],
  [/(_zip|^cardZip)$/, { autoComplete: 'postal-code', inputMode: 'numeric' }],
  [/^business_website$/, { autoComplete: 'url', inputMode: 'url' }],
  [/_ssn$/, { autoComplete: 'off', inputMode: 'numeric' }],
  [/_ownership$/, { inputMode: 'decimal' }],
  [/^business_ein$/, { inputMode: 'numeric' }],
  [/^(loan_amount|business_revenue|annual_income)$/, { inputMode: 'decimal' }],
];

/**
 * @param {object} field
 * @param {string} [field.name] redux-form field name
 * @param {string} [field.type] the explicit input type, if any
 * @param {boolean} [field.formatAsNumber] currency mask
 * @param {boolean} [field.formatAsPhone] ###-###-#### mask
 * @param {boolean} [field.formatAsFederalTaxId] XX-XXXXXXX mask
 * @param {boolean} [field.formatAsSsn] SSN input
 * @returns {{ autoComplete?: string, inputMode?: string, type?: string }} defaults; empty when nothing is known
 */
export const autofillDefaults = ({
  name = '',
  type,
  formatAsNumber,
  formatAsPhone,
  formatAsFederalTaxId,
  formatAsSsn,
} = {}) => {
  const byName = NAME_RULES.find(([pattern]) => pattern.test(name))?.[1] ?? {};
  const byFormat = formatAsPhone
    ? { autoComplete: 'tel', inputMode: 'tel', type: 'tel' }
    : formatAsNumber
    ? { inputMode: 'decimal' }
    : formatAsFederalTaxId
    ? { inputMode: 'numeric' }
    : formatAsSsn
    ? { autoComplete: 'off', inputMode: 'numeric' }
    : type === 'email'
    ? { autoComplete: 'email', inputMode: 'email' }
    : type === 'tel'
    ? { inputMode: 'tel' }
    : {};
  return { ...byFormat, ...byName };
};
